import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import PdaPage from '../components/PdaPage';
import PmiElementComparison, {
  PmiElementComparisonItem,
  PmiElementFilter,
  PmiElementState,
} from '../components/PmiElementComparison';
import {
  bindPmiSpectrum,
  CustomerTechSpecElementItem,
  CustomerTechSpecElementListData,
  FinalReleasePmiRequirement,
  listCustomerTechSpecElements,
  PmiScanResult,
  PmiUnit,
  listFinalReleasePmiRequirementUnits,
  listFinalReleasePmiRequirements,
  scanPmiCode,
} from '../services/api';
import { navigateTo } from '../utils/navigation';
import { notifyError } from '../utils/pdaFeedback';
import {
  buildPmiDisplayState,
  buildPmiRequirementPoolItem,
  buildPmiScanResultFromRequirementUnits,
  getPmiUnitSerial,
  PmiDisplayState,
  PmiRequirementPoolItem,
  selectPmiScanResultUnit,
} from '../utils/pmiScan';
import { normalizeScannedCode } from '../utils/pdaCodes';
import { getStoredUser } from '../utils/session';
import {
  DEFAULT_PMI_SPECTRUM_WS_URL,
  buildPmiSpectrumPayloadKey,
  buildPmiSpectrumPayloadFromWsData,
  buildPmiSpectrumWsUrl,
  isPmiSpectrumWsDataFresh,
} from '../utils/xrfSpectrum';

const PMI_SPECTRUM_WS_URL = process.env.REACT_APP_PMI_SPECTRUM_WS_URL
  || DEFAULT_PMI_SPECTRUM_WS_URL;
const PMI_SPECTRUM_DEVICES_URL = process.env.REACT_APP_PMI_SPECTRUM_DEVICES_URL || 'https://api.yanclex.com/devices';
const PMI_SPECTRUM_DEDUPE_WINDOW_MS = 15000;
const PMI_TASK_POOL_PAGE_SIZE = 20;
const PMI_TASK_POOL_MAX_PAGES = 50;
const PMI_FAIL_EFFECT_MS = 1800;

interface PmiTaskCard {
  key: string;
  display: PmiRequirementPoolItem;
  result?: PmiScanResult;
  scanCode: string;
}

interface XrfDevice {
  deviceId: string;
}

type PmiUnitStateTone = 'active' | 'complete' | 'fail' | 'pass' | 'pending';

type PmiElementDetailItem = PmiElementComparisonItem;

interface PmiElementRange {
  code?: string;
  max?: number;
  min?: number;
}

interface PmiUnitDisplayState {
  text: string;
  tone: PmiUnitStateTone;
}

interface PmiPostResultAction {
  nextSerial: string;
  result: 'fail' | 'pass';
  serial: string;
}

interface PmiResultEffectState {
  result: 'fail' | 'pass';
  serial: string;
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || value instanceof Blob || value instanceof ArrayBuffer) {
    return null;
  }
  return value as Record<string, unknown>;
}

function getTaskCardKey(result: PmiScanResult, display: PmiDisplayState) {
  return String(
    result.task?.id
      || result.task_no
      || result.pmi_task_no
      || display.taskNo
      || display.serial,
  );
}

function extractPmiRequirementList(data?: {
  items?: FinalReleasePmiRequirement[];
  list?: FinalReleasePmiRequirement[];
  data?: FinalReleasePmiRequirement[];
}) {
  const items = data?.items;
  const list = data?.list;
  const nestedData = data?.data;

  if (Array.isArray(items)) {
    return items;
  }
  if (Array.isArray(list)) {
    return list;
  }
  if (Array.isArray(nestedData)) {
    return nestedData;
  }
  return [];
}

function extractPmiRequirementTotal(data?: { total?: number | string }) {
  return asNumber(data?.total);
}

async function listAllFinalReleasePmiRequirements() {
  const requirements: FinalReleasePmiRequirement[] = [];
  let page = 1;
  let total = 0;

  while (page <= PMI_TASK_POOL_MAX_PAGES) {
    const response = await listFinalReleasePmiRequirements({
      page,
      page_size: PMI_TASK_POOL_PAGE_SIZE,
    });
    const pageItems = extractPmiRequirementList(response.data);
    requirements.push(...pageItems);
    total = Math.max(total, extractPmiRequirementTotal(response.data));

    if ((total > 0 && requirements.length >= total) || pageItems.length < PMI_TASK_POOL_PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return total > 0 ? requirements.slice(0, total) : requirements;
}

function extractCustomerTechSpecElements(data?: CustomerTechSpecElementItem[] | CustomerTechSpecElementListData) {
  if (Array.isArray(data)) {
    return data;
  }

  const elements = data?.elements;
  const items = data?.items;
  const list = data?.list;
  const nestedData = data?.data;

  if (Array.isArray(elements)) {
    return elements;
  }
  if (Array.isArray(items)) {
    return items;
  }
  if (Array.isArray(list)) {
    return list;
  }
  if (Array.isArray(nestedData)) {
    return nestedData;
  }
  return [];
}

function createScannedTaskCard(result: PmiScanResult): PmiTaskCard | null {
  const display = buildPmiDisplayState(result);
  if (!display) {
    return null;
  }

  const key = getTaskCardKey(result, display);
  const requirementId = getScanResultRequirementId(result);
  const status = display.completed > 0 && display.completed < display.total
    ? { text: '部分检测', tone: 'partial' as const }
    : display.completed >= display.total
      ? { text: '已完成', tone: 'complete' as const }
      : { text: '待检测', tone: 'pending' as const };

  return {
    display: {
      batchNo: display.taskNo,
      completed: display.completed,
      currentQuantity: display.total,
      customerCode: '',
      customerTechSpecId: asText(
        result.task?.customer_tech_spec_id
          || result.current_unit?.customer_tech_spec_id
          || result.customer_tech_spec_id,
      ),
      drawingNo: '',
      failedCount: 0,
      grade: display.grade,
      itemNo: '',
      key,
      materialNo: '',
      orderNo: '',
      orderQuantity: display.total,
      passedCount: display.completed,
      pmiDataText: '',
      pmiInspection: asText(
        result.current_unit?.pmi_inspection
          || result.task?.pmi_inspection
          || result.pmi_inspection,
      ),
      productDescription: '',
      requirementId,
      requirementText: '',
      scanCode: display.serial,
      statusText: status.text,
      statusTone: status.tone,
      taskNo: display.taskNo || display.serial,
      total: display.total,
    },
    key,
    result,
    scanCode: display.serial,
  };
}

function getScanResultRequirementId(result: PmiScanResult) {
  return asText(
    result.task?.id
      || result.task?.pmi_requirement_id
      || result.task?.requirement_id
      || result.current_unit?.pmi_requirement_id
      || result.current_unit?.requirement_id
      || result.pmi_requirement_id
      || result.requirement_id,
  );
}

function findTaskCardByRequirementId(cards: PmiTaskCard[], requirementId: string) {
  const normalizedId = requirementId.trim();
  if (!normalizedId) {
    return null;
  }

  return cards.find((card) => (
    card.key === normalizedId || card.display.requirementId === normalizedId
  )) || null;
}

function findTaskCardByScanResult(cards: PmiTaskCard[], result: PmiScanResult, code?: string) {
  const requirementId = getScanResultRequirementId(result);
  const matchedByRequirement = findTaskCardByRequirementId(cards, requirementId);
  if (matchedByRequirement) {
    return matchedByRequirement;
  }

  const taskCode = asText(
    result.task_no
      || result.pmi_task_no
      || result.task?.task_no
      || result.task?.pmi_task_no,
  );
  if (taskCode) {
    const matchedByTaskCode = findTaskCardByCode(cards, taskCode);
    if (matchedByTaskCode) {
      return matchedByTaskCode;
    }
  }

  return code && isPmiTaskScanCode(code) ? findTaskCardByCode(cards, code) : null;
}

function applyTaskCardMetadataToScanResult(result: PmiScanResult, card: PmiTaskCard): PmiScanResult {
  const display = card.display;
  const enrichUnit = (unit: PmiUnit): PmiUnit => ({
    ...unit,
    grade: asText(unit.grade) || display.grade,
    material_grade: asText(unit.material_grade) || display.grade,
    pmi_requirement_id: asText(unit.pmi_requirement_id) || display.requirementId,
    task_no: asText(unit.task_no) || display.taskNo,
  });

  return {
    ...result,
    current_unit: enrichUnit(result.current_unit),
    grade: asText(result.grade) || display.grade,
    material_grade: asText(result.material_grade) || display.grade,
    task: {
      ...result.task,
      id: result.task?.id || display.requirementId,
      item_no: result.task?.item_no || display.itemNo,
      material_grade: result.task?.material_grade || display.grade,
      material_no: result.task?.material_no || display.materialNo,
      order_no: result.task?.order_no || display.orderNo,
      task_no: result.task?.task_no || display.taskNo,
    },
    task_no: asText(result.task_no) || display.taskNo,
    units: Array.isArray(result.units) ? result.units.map(enrichUnit) : result.units,
  };
}

function mergeTaskCards(cards: PmiTaskCard[], result: PmiScanResult) {
  const nextCard = createScannedTaskCard(result);
  if (!nextCard) {
    return cards;
  }

  return [
    nextCard,
    ...cards.filter((card) => card.key !== nextCard.key),
  ].slice(0, 8);
}

function isScanResult(value: unknown): value is PmiScanResult {
  const record = toRecord(value);
  return Boolean(record?.current_unit);
}

function normalizeTaskMatchCode(value: string) {
  return normalizeScannedCode(value).trim().toUpperCase();
}

function isPmiTaskScanCode(code: string) {
  return /^PMIT-\d{8}-\d+$/i.test(code) || /^PMI_TASK:\d+$/i.test(code);
}

function getTaskCardMatchCodes(card: PmiTaskCard) {
  return [
    card.scanCode,
    card.display.scanCode,
    card.display.taskNo,
    card.display.batchNo,
    card.display.requirementId ? `PMI_TASK:${card.display.requirementId}` : '',
  ].filter(Boolean);
}

function findTaskCardByCode(cards: PmiTaskCard[], code: string) {
  const target = normalizeTaskMatchCode(code);
  if (!target) {
    return null;
  }

  return cards.find((card) => (
    getTaskCardMatchCodes(card).some((matchCode) => normalizeTaskMatchCode(matchCode) === target)
  )) || null;
}

function asText(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function asNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
}

function asOptionalNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const normalized = value.trim().replace('%', '');
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  return undefined;
}

function parseJsonValue(value: unknown) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function readText(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = asText(record[key]).trim();
    if (value) {
      return value;
    }
  }
  return '';
}

function readOptionalNumber(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = asOptionalNumber(record[key]);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function formatPmiNumber(value: number) {
  return value.toFixed(3);
}

function formatPmiValue(value: unknown) {
  const numeric = asOptionalNumber(value);
  if (numeric !== undefined) {
    return `${formatPmiNumber(numeric)}%`;
  }

  return asText(value) || '--';
}

function formatPmiRange(min?: number, max?: number) {
  if (min !== undefined && max !== undefined) {
    return `要求 ${formatPmiNumber(min)} - ${formatPmiNumber(max)}%`;
  }
  if (min !== undefined) {
    return `要求 ≥ ${formatPmiNumber(min)}%`;
  }
  if (max !== undefined) {
    return `要求 ≤ ${formatPmiNumber(max)}%`;
  }
  return '无 PMI 区间，仅展示';
}

function normalizeElementKey(value: unknown) {
  return asText(value).trim().toUpperCase();
}

function parseRangeText(value: unknown): PmiElementRange | null {
  const text = asText(value).trim();
  if (!text) {
    return null;
  }

  const rangeMatch = text.match(/(-?\d+(?:\.\d+)?)\s*(?:-|~|–|—|至|到)\s*(-?\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      max: asOptionalNumber(rangeMatch[2]),
      min: asOptionalNumber(rangeMatch[1]),
    };
  }

  const minMatch = text.match(/(?:min|下限|≥|>=)\s*[:：]?\s*(-?\d+(?:\.\d+)?)/i);
  const maxMatch = text.match(/(?:max|上限|≤|<=)\s*[:：]?\s*(-?\d+(?:\.\d+)?)/i);
  const min = minMatch ? asOptionalNumber(minMatch[1]) : undefined;
  const max = maxMatch ? asOptionalNumber(maxMatch[1]) : undefined;
  return min !== undefined || max !== undefined ? { max, min } : null;
}

function readPmiRangeFromRecord(record: Record<string, unknown>): PmiElementRange | null {
  const min = readOptionalNumber(record, ['pmi_min', 'pmiMin', 'min', 'min_value', 'lower_limit', 'minimum']);
  const max = readOptionalNumber(record, ['pmi_max', 'pmiMax', 'max', 'max_value', 'upper_limit', 'maximum']);
  if (min !== undefined || max !== undefined) {
    return { max, min };
  }

  return parseRangeText(record.range || record.pmi_range || record.requirement || record.value);
}

function putPmiRange(rangeMap: Map<string, PmiElementRange>, code: unknown, range: PmiElementRange | null) {
  const key = normalizeElementKey(code);
  if (!key || !range || (range.min === undefined && range.max === undefined)) {
    return;
  }
  rangeMap.set(key, {
    ...range,
    code: asText(code).trim() || range.code || key,
  });
}

function addPmiRangeSource(rangeMap: Map<string, PmiElementRange>, source: unknown) {
  const parsed = parseJsonValue(source);
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      const record = toRecord(item);
      if (record) {
        putPmiRange(rangeMap, readText(record, ['symbol', 'element', 'element_code', 'code', 'name']), readPmiRangeFromRecord(record));
      }
    });
    return;
  }

  const record = toRecord(parsed);
  if (!record) {
    const text = asText(parsed);
    const rangeRegex = /([A-Za-z][A-Za-z0-9]*)\s*[:：]?\s*(-?\d+(?:\.\d+)?)\s*(?:-|~|–|—|至|到)\s*(-?\d+(?:\.\d+)?)/g;
    let match = rangeRegex.exec(text);
    while (match) {
      putPmiRange(rangeMap, match[1], {
        max: asOptionalNumber(match[3]),
        min: asOptionalNumber(match[2]),
      });
      match = rangeRegex.exec(text);
    }
    return;
  }

  const nested = record.elements || record.items || record.list || record.data;
  if (Array.isArray(nested)) {
    addPmiRangeSource(rangeMap, nested);
  }

  Object.entries(record).forEach(([key, value]) => {
    const valueRecord = toRecord(value);
    if (valueRecord) {
      putPmiRange(
        rangeMap,
        readText(valueRecord, ['symbol', 'element', 'element_code', 'code', 'name']) || key,
        readPmiRangeFromRecord(valueRecord),
      );
      return;
    }

    if (Array.isArray(value) && value.length >= 2) {
      putPmiRange(rangeMap, key, {
        max: asOptionalNumber(value[1]),
        min: asOptionalNumber(value[0]),
      });
      return;
    }

    putPmiRange(rangeMap, key, parseRangeText(value));
  });
}

function buildPmiRangeMap(...sources: unknown[]) {
  const rangeMap = new Map<string, PmiElementRange>();
  sources.forEach((source) => addPmiRangeSource(rangeMap, source));
  return rangeMap;
}

function buildMissingPmiRangeItems(
  detectedItems: PmiElementDetailItem[],
  rangeMap: Map<string, PmiElementRange>,
) {
  const detectedCodes = new Set(detectedItems.map((item) => normalizeElementKey(item.code)));

  return Array.from(rangeMap.entries()).reduce<PmiElementDetailItem[]>((items, [key, range]) => {
    if (detectedCodes.has(key)) {
      return items;
    }

    items.push({
      code: range.code || key,
      rangeText: formatPmiRange(range.min, range.max),
      state: 'fail',
      stateText: '未检测',
      valueText: '未检出',
    });
    return items;
  }, []);
}

function sortDetectedPmiElements(items: PmiElementDetailItem[]) {
  return [...items].sort((first, second) => {
    if (first.state === second.state) {
      return 0;
    }
    if (first.state === 'fail') {
      return -1;
    }
    if (second.state === 'fail') {
      return 1;
    }
    return first.state === 'pass' ? -1 : 1;
  });
}

function hasPmiRangeSource(source: unknown) {
  return buildPmiRangeMap(source).size > 0;
}

function serializeTechSpecPmiRanges(items: CustomerTechSpecElementItem[]) {
  const records = items.map(toRecord).filter((record): record is Record<string, unknown> => Boolean(record));
  const hasRequiredFlags = records.some((record) => (
    record.pmi_required !== undefined && record.pmi_required !== null && record.pmi_required !== ''
  ));
  const rangeItems = records
    .map((record) => {
      const requiredText = asText(record.pmi_required).trim().toLowerCase();
      const isRequired = record.pmi_required === true
        || record.pmi_required === 1
        || ['1', 'true', 'yes', 'required', 'enabled', '是', '启用'].includes(requiredText);
      if (hasRequiredFlags && !isRequired) {
        return null;
      }
      const code = readText(record, ['element', 'symbol', 'element_code', 'code', 'name']);
      const range = readPmiRangeFromRecord(record);
      if (!code || !range || (range.min === undefined && range.max === undefined)) {
        return null;
      }

      return {
        element: code,
        pmi_max: range.max,
        pmi_min: range.min,
      };
    })
    .filter(Boolean) as Array<{ element: string; pmi_max: number | undefined; pmi_min: number | undefined }>;

  return rangeItems.length ? JSON.stringify(rangeItems) : '';
}

function applyPmiInspectionSource(result: PmiScanResult, pmiInspection: string): PmiScanResult {
  if (!pmiInspection || !hasPmiRangeSource(pmiInspection)) {
    return result;
  }

  const currentUnit = result.current_unit;
  const nextCurrentUnit = {
    ...currentUnit,
    pmi_inspection: hasPmiRangeSource(currentUnit?.pmi_inspection) ? currentUnit.pmi_inspection : pmiInspection,
  };

  return {
    ...result,
    current_unit: nextCurrentUnit,
    pmi_inspection: hasPmiRangeSource(result.pmi_inspection) ? result.pmi_inspection : pmiInspection,
    task: result.task ? {
      ...result.task,
      pmi_inspection: hasPmiRangeSource(result.task.pmi_inspection) ? result.task.pmi_inspection : pmiInspection,
    } : result.task,
    units: Array.isArray(result.units)
      ? result.units.map((unit) => ({
        ...unit,
        pmi_inspection: hasPmiRangeSource(unit.pmi_inspection) ? unit.pmi_inspection : pmiInspection,
      }))
      : result.units,
  };
}

function getElementState(
  record: Record<string, unknown>,
  value?: number,
  min?: number,
  max?: number,
): PmiElementState {
  const explicit = record.passed ?? record.pass ?? record.in_range ?? record.qualified;
  if (typeof explicit === 'boolean') {
    return explicit ? 'pass' : 'fail';
  }
  if (typeof explicit === 'number') {
    return explicit ? 'pass' : 'fail';
  }
  const explicitText = asText(explicit).trim().toLowerCase();
  if (['true', '1', 'pass', 'passed', 'ok', 'qualified', '合格'].includes(explicitText)) {
    return 'pass';
  }
  if (['false', '0', 'fail', 'failed', 'ng', 'unqualified', '不合格'].includes(explicitText)) {
    return 'fail';
  }

  const resultText = readText(record, ['result', 'state', 'status', 'final_result']).toLowerCase();
  if (['pass', 'passed', 'ok', 'qualified', '合格'].includes(resultText)) {
    return 'pass';
  }
  if (['fail', 'failed', 'ng', 'unqualified', '不合格'].includes(resultText)) {
    return 'fail';
  }

  if (value !== undefined && (min !== undefined || max !== undefined)) {
    if (min !== undefined && value < min) {
      return 'fail';
    }
    if (max !== undefined && value > max) {
      return 'fail';
    }
    return 'pass';
  }

  return 'pending';
}

function normalizePmiElementRecord(item: unknown, rangeMap?: Map<string, PmiElementRange>): PmiElementDetailItem | null {
  const record = toRecord(item);
  if (!record) {
    return null;
  }

  const code = readText(record, ['symbol', 'element', 'element_code', 'code', 'name']);
  if (!code) {
    return null;
  }

  const rawValue = record.value
    ?? record.measured_value
    ?? record.concentration
    ?? record.content
    ?? record.result_value
    ?? record.percent;
  const value = asOptionalNumber(rawValue);
  const fallbackRange = rangeMap?.get(normalizeElementKey(code));
  const min = readOptionalNumber(record, ['pmi_min', 'min', 'min_value', 'lower_limit', 'minimum']) ?? fallbackRange?.min;
  const max = readOptionalNumber(record, ['pmi_max', 'max', 'max_value', 'upper_limit', 'maximum']) ?? fallbackRange?.max;
  const state = getElementState(record, value, min, max);

  return {
    code,
    rangeText: formatPmiRange(min, max),
    state,
    stateText: state === 'pass' ? '合格' : state === 'fail' ? '不合格' : '未判定',
    valueText: formatPmiValue(rawValue),
  };
}

function normalizePmiElements(source: unknown, rangeMap?: Map<string, PmiElementRange>): PmiElementDetailItem[] {
  const parsed = parseJsonValue(source);
  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => normalizePmiElementRecord(item, rangeMap))
      .filter((item): item is PmiElementDetailItem => Boolean(item));
  }

  const record = toRecord(parsed);
  if (!record) {
    return [];
  }

  return Object.entries(record).map(([code, value]) => normalizePmiElementRecord({
    code,
    value,
  }, rangeMap)).filter((item): item is PmiElementDetailItem => Boolean(item));
}

function getPmiElementDetails(unit?: PmiUnit | null) {
  if (!unit) {
    return [];
  }

  const sources = [
    unit.element_results,
    unit.spectrum_elements,
    unit.pmi_elements,
    unit.analysis_elements,
    unit.inspection_elements,
    unit.elements,
  ];
  const rangeMap = buildPmiRangeMap(
    unit.pmi_inspection,
    unit.pmiInspection,
    unit.pmi_ranges,
    unit.pmiRange,
    unit.pmi_range,
    unit.requirement_elements,
    unit.pmi_requirement_elements,
  );

  for (const source of sources) {
    const items = normalizePmiElements(source, rangeMap);
    if (items.length) {
      return [
        ...sortDetectedPmiElements(items),
        ...buildMissingPmiRangeItems(items, rangeMap),
      ];
    }
  }

  return [];
}

function extractXrfDevices(data: unknown) {
  const record = toRecord(data);
  const source = Array.isArray(data) ? data : record?.devices;
  if (!Array.isArray(source)) {
    return [];
  }

  return source.reduce<XrfDevice[]>((devices, item) => {
    const device = toRecord(item);
    const deviceId = asText(device?.deviceId || device?.device_id || device?.id).trim();
    if (!deviceId) {
      return devices;
    }

    devices.push({ deviceId });
    return devices;
  }, []);
}

function isDisplayUnitBound(unit: PmiUnit) {
  const status = asText(unit.status).toLowerCase();
  const bindStatus = asText(unit.bind_status).toLowerCase();
  return Boolean(
    unit.is_bound
      || unit.has_spectrum
      || unit.bind_at
      || unit.bound_at
      || unit.spectrum_id
      || unit.spectrum_file_id
      || unit.spectrum_result_id
      || ['bound', 'done', 'completed', 'finished'].includes(bindStatus)
      || ['bound', 'done', 'completed', 'finished'].includes(status),
  );
}

function readPmiUnitExplicitResult(unit: PmiUnit): PmiElementState | null {
  const record = unit as Record<string, unknown>;
  const explicit = record.is_pass
    ?? record.isPass
    ?? record.passed
    ?? record.pass
    ?? record.qualified
    ?? record.is_qualified
    ?? record.isQualified
    ?? record.in_range
    ?? record.inRange;

  if (typeof explicit === 'boolean') {
    return explicit ? 'pass' : 'fail';
  }
  if (typeof explicit === 'number') {
    return explicit ? 'pass' : 'fail';
  }

  const explicitText = asText(explicit).trim().toLowerCase();
  if (['true', '1', 'pass', 'passed', 'ok', 'qualified', '合格'].includes(explicitText)) {
    return 'pass';
  }
  if (['false', '0', 'fail', 'failed', 'ng', 'unqualified', '不合格'].includes(explicitText)) {
    return 'fail';
  }

  const resultText = readText(record, [
    'final_result',
    'finalResult',
    'inspection_result',
    'inspectionResult',
    'pmi_result',
    'pmiResult',
    'quality_result',
    'qualityResult',
    'overall_result',
    'overallResult',
    'judge_result',
    'judgeResult',
    'judgement',
    'judgment',
    'conclusion',
    'result',
  ]).toLowerCase();
  if (['pass', 'passed', 'ok', 'qualified', '合格'].includes(resultText)) {
    return 'pass';
  }
  if (['fail', 'failed', 'ng', 'unqualified', '不合格'].includes(resultText)) {
    return 'fail';
  }

  const failCount = readOptionalNumber(record, [
    'failed_count',
    'failedCount',
    'fail_count',
    'failCount',
    'ng_count',
    'ngCount',
    'unqualified_count',
    'unqualifiedCount',
    'abnormal_count',
    'abnormalCount',
  ]);
  if (failCount !== undefined && failCount > 0) {
    return 'fail';
  }

  const passCount = readOptionalNumber(record, [
    'passed_count',
    'passedCount',
    'pass_count',
    'passCount',
    'qualified_count',
    'qualifiedCount',
  ]);
  if (passCount !== undefined && passCount > 0 && (failCount === undefined || failCount === 0)) {
    return 'pass';
  }

  return null;
}

function inferPmiUnitResultFromElements(unit: PmiUnit): PmiElementState | null {
  const items = getPmiElementDetails(unit);
  const hasFail = items.some((item) => item.state === 'fail');
  if (hasFail) {
    return 'fail';
  }

  const hasPass = items.some((item) => item.state === 'pass');
  return hasPass ? 'pass' : null;
}

function getPmiUnitResult(unit: PmiUnit): PmiElementState | null {
  const inferredResult = inferPmiUnitResultFromElements(unit);
  const explicitResult = readPmiUnitExplicitResult(unit);
  return inferredResult === 'fail' ? 'fail' : explicitResult || inferredResult;
}

function findPmiUnitBySerial(result: PmiScanResult, serial: string) {
  const target = serial.trim().toUpperCase();
  const units = Array.isArray(result.units) ? result.units : [];
  return units.find((unit) => getPmiUnitSerial(unit).trim().toUpperCase() === target)
    || (getPmiUnitSerial(result.current_unit).trim().toUpperCase() === target ? result.current_unit : undefined);
}

function getPmiUnitDisplayState(unit: PmiUnit, active: boolean, spectrumWaiting: boolean): PmiUnitDisplayState {
  const bound = isDisplayUnitBound(unit);
  if (!bound) {
    if (active) {
      return {
        text: spectrumWaiting ? '当前等待' : '待开始',
        tone: 'active',
      };
    }
    return {
      text: '待检测',
      tone: 'pending',
    };
  }

  const result = getPmiUnitResult(unit);
  if (result === 'fail') {
    return {
      text: '不合格',
      tone: 'fail',
    };
  }
  if (result === 'pass') {
    return {
      text: '合格',
      tone: 'pass',
    };
  }
  return {
    text: '已完成',
    tone: 'complete',
  };
}

function getDetailUnitLabel(unit: PmiUnit, index: number) {
  return asText(unit.position_no || unit.unit_no || unit.piece_no || unit.seq_no || unit.index) || `第 ${index + 1} 件`;
}

function findNextPendingUnitSerial(result: PmiScanResult, completedSerial: string) {
  const units = Array.isArray(result.units) ? result.units : [];
  if (!units.length) {
    return '';
  }

  const completedTarget = completedSerial.trim().toUpperCase();
  const currentIndex = Math.max(units.findIndex((unit) => (
    getPmiUnitSerial(unit).trim().toUpperCase() === completedTarget
  )), -1);
  const orderedUnits = currentIndex >= 0
    ? [...units.slice(currentIndex + 1), ...units.slice(0, currentIndex + 1)]
    : units;
  const nextUnit = orderedUnits.find((unit) => {
    const serial = getPmiUnitSerial(unit);
    return serial && serial.trim().toUpperCase() !== completedTarget && !isDisplayUnitBound(unit);
  });

  return nextUnit ? getPmiUnitSerial(nextUnit) : '';
}

function PmiScan() {
  const user = getStoredUser();
  const [scanValue, setScanValue] = useState('');
  const [scanResult, setScanResult] = useState<PmiScanResult | null>(null);
  const [activeTaskCard, setActiveTaskCard] = useState<PmiTaskCard | null>(null);
  const [taskCards, setTaskCards] = useState<PmiTaskCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [poolLoading, setPoolLoading] = useState(false);
  const [binding, setBinding] = useState(false);
  const [message, setMessage] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [xrfDevices, setXrfDevices] = useState<XrfDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [spectrumWaiting, setSpectrumWaiting] = useState(false);
  const [showingElementDetail, setShowingElementDetail] = useState(false);
  const [elementFilter, setElementFilter] = useState<PmiElementFilter>('all');
  const [postResultAction, setPostResultAction] = useState<PmiPostResultAction | null>(null);
  const [resultEffect, setResultEffect] = useState<PmiResultEffectState | null>(null);
  const showingDetail = Boolean(activeTaskCard);
  const currentDisplay = useMemo(() => buildPmiDisplayState(scanResult), [scanResult]);
  const detailUnits = useMemo(() => (scanResult && Array.isArray(scanResult.units) ? scanResult.units : []), [scanResult]);
  const currentElementDetails = useMemo(() => getPmiElementDetails(scanResult?.current_unit), [scanResult]);
  const elementPassCount = currentElementDetails.filter((item) => item.state === 'pass').length;
  const elementFailCount = currentElementDetails.filter((item) => item.state === 'fail').length;
  const explicitCurrentUnitResult = scanResult?.current_unit ? getPmiUnitResult(scanResult.current_unit) : null;
  const elementResultState: PmiElementState = elementFailCount
    ? 'fail'
    : elementPassCount && elementPassCount === currentElementDetails.length
      ? 'pass'
      : explicitCurrentUnitResult || 'pending';
  const currentTaskCompleted = Boolean(currentDisplay && !currentDisplay.waiting && currentDisplay.remaining <= 0);
  const selectedDeviceText = selectedDeviceId || '暂无光谱仪';
  const currentDisplayRef = useRef<PmiDisplayState | null>(null);
  const activeUnitRef = useRef<HTMLDivElement | null>(null);
  const bindSpectrumRef = useRef<(raw: unknown) => Promise<void>>(async () => undefined);
  const bindingRef = useRef(false);
  const consumedSpectrumKeysRef = useRef<Map<string, number>>(new Map());
  const pmiInspectionCacheRef = useRef<Map<string, string>>(new Map());
  const resultEffectTimerRef = useRef<number | null>(null);
  const spectrumWaitingRef = useRef(false);
  const waitingSinceRef = useRef(Date.now());

  currentDisplayRef.current = currentDisplay;

  function markWaitingFromNow() {
    waitingSinceRef.current = Date.now();
  }

  function setSpectrumWaitingActive(nextWaiting: boolean) {
    spectrumWaitingRef.current = nextWaiting;
    setSpectrumWaiting(nextWaiting);
  }

  function clearResultEffectTimer() {
    if (resultEffectTimerRef.current) {
      window.clearTimeout(resultEffectTimerRef.current);
      resultEffectTimerRef.current = null;
    }
  }

  function resetResultEffect() {
    clearResultEffectTimer();
    setResultEffect(null);
  }

  function openElementDetailAfterResult(
    selectedResult: PmiScanResult,
    completedSerial: string,
    result: 'fail' | 'pass',
  ) {
    resetResultEffect();
    setShowingElementDetail(false);
    setResultEffect({
      result,
      serial: completedSerial,
    });
    resultEffectTimerRef.current = window.setTimeout(() => {
      resultEffectTimerRef.current = null;
      setResultEffect(null);
      setShowingElementDetail(true);
    }, PMI_FAIL_EFFECT_MS);
  }

  async function resolvePmiInspectionSource(display: Pick<PmiRequirementPoolItem, 'customerTechSpecId' | 'pmiInspection'>) {
    if (hasPmiRangeSource(display.pmiInspection)) {
      return display.pmiInspection;
    }

    const techSpecId = display.customerTechSpecId.trim();
    if (!techSpecId) {
      return display.pmiInspection;
    }

    const cached = pmiInspectionCacheRef.current.get(techSpecId);
    if (cached !== undefined) {
      return cached || display.pmiInspection;
    }

    try {
      const response = await listCustomerTechSpecElements(techSpecId);
      const pmiInspection = serializeTechSpecPmiRanges(extractCustomerTechSpecElements(response.data));
      pmiInspectionCacheRef.current.set(techSpecId, pmiInspection);
      return pmiInspection || display.pmiInspection;
    } catch (error) {
      pmiInspectionCacheRef.current.set(techSpecId, '');
      return display.pmiInspection;
    }
  }

  function hasRecentlyConsumedSpectrumKey(key: string) {
    const now = Date.now();
    const consumedKeys = consumedSpectrumKeysRef.current;
    consumedKeys.forEach((consumedAt, consumedKey) => {
      if (now - consumedAt > PMI_SPECTRUM_DEDUPE_WINDOW_MS) {
        consumedKeys.delete(consumedKey);
      }
    });
    return consumedKeys.has(key);
  }

  function markSpectrumKeyConsumed(key: string) {
    consumedSpectrumKeysRef.current.set(key, Date.now());
  }

  async function loadTaskPool() {
    setPoolLoading(true);
    try {
      const cards = (await listAllFinalReleasePmiRequirements())
        .map(buildPmiRequirementPoolItem)
        .filter((item): item is PmiRequirementPoolItem => Boolean(item))
        .map<PmiTaskCard>((item) => ({
          display: item,
          key: item.key,
          scanCode: item.scanCode,
        }));

      setTaskCards((currentCards) => {
        const scannedCards = currentCards.filter((card) => card.result);
        return [
          ...cards,
          ...scannedCards.filter((card) => !cards.some((item) => item.key === card.key)),
        ];
      });
    } catch (error: any) {
      notifyError(error?.msg || 'PMI 任务池加载失败');
    } finally {
      setPoolLoading(false);
    }
  }

  async function loadXrfDevices() {
    if (typeof fetch !== 'function') {
      return;
    }

    setDeviceLoading(true);
    try {
      const response = await fetch(PMI_SPECTRUM_DEVICES_URL);
      if (!response.ok) {
        throw new Error('光谱仪设备列表加载失败');
      }

      const devices = extractXrfDevices(await response.json());
      setXrfDevices(devices);
      setSelectedDeviceId((currentDeviceId) => {
        return devices.some((device) => device.deviceId === currentDeviceId)
          ? currentDeviceId
          : devices[0]?.deviceId || '';
      });
    } catch {
      setXrfDevices([]);
      setSelectedDeviceId('');
    } finally {
      setDeviceLoading(false);
    }
  }

  function cacheActiveTaskResult(nextResult: PmiScanResult) {
    if (!activeTaskCard) {
      return false;
    }

    const resultWithRanges = applyPmiInspectionSource(nextResult, activeTaskCard.display.pmiInspection);
    const nextCard = { ...activeTaskCard, result: resultWithRanges };
    setActiveTaskCard(nextCard);
    setTaskCards((cards) => cards.map((card) => (
      card.key === activeTaskCard.key ? nextCard : card
    )));
    return true;
  }

  function syncScanResult(nextResult: PmiScanResult) {
    const resultWithRanges = applyPmiInspectionSource(nextResult, activeTaskCard?.display.pmiInspection || '');
    setScanResult(resultWithRanges);
    if (!cacheActiveTaskResult(resultWithRanges)) {
      setTaskCards((cards) => mergeTaskCards(cards, resultWithRanges));
    }
  }

  function syncCompletedResultForElementDetail(
    nextResult: PmiScanResult,
    completedSerial: string,
    result: 'fail' | 'pass',
  ) {
    const selectedResult = selectPmiScanResultUnit(nextResult, completedSerial) || nextResult;
    const nextSerial = findNextPendingUnitSerial(nextResult, completedSerial);
    syncScanResult(selectedResult);
    markWaitingFromNow();
    setSpectrumWaitingActive(false);
    setElementFilter(result === 'fail' ? 'fail' : 'all');
    setPostResultAction({
      nextSerial,
      result,
      serial: completedSerial,
    });
    openElementDetailAfterResult(selectedResult, completedSerial, result);
    return nextSerial;
  }

  function selectCurrentTaskUnit(serial: string, options?: { forceAwaiting?: boolean; ignorePostResultAction?: boolean }) {
    if (
      !options?.ignorePostResultAction
      && postResultAction?.result === 'fail'
      && postResultAction.serial.trim().toUpperCase() !== serial.trim().toUpperCase()
    ) {
      notifyError('请先人工确认不合格件');
      return false;
    }

    const selectedResult = selectPmiScanResultUnit(scanResult, serial, options);
    if (!selectedResult) {
      return false;
    }

    setScanResult(selectedResult);
    cacheActiveTaskResult(selectedResult);
    markWaitingFromNow();
    setSpectrumWaitingActive(false);
    setShowingElementDetail(false);
    setElementFilter('all');
    setPostResultAction(null);
    resetResultEffect();
    setMessage('');
    setScanValue('');
    return true;
  }

  function handlePostResultContinue() {
    if (!postResultAction) {
      return;
    }

    const { nextSerial, result, serial } = postResultAction;
    setPostResultAction(null);
    if (nextSerial && selectCurrentTaskUnit(nextSerial, { ignorePostResultAction: true })) {
      setMessage(result === 'fail'
        ? `${serial} 已人工确认，已切到 ${nextSerial}。`
        : `${serial} 已查看，已切到 ${nextSerial}。`);
      return;
    }

    setSpectrumWaitingActive(false);
    setShowingElementDetail(false);
    setElementFilter('all');
    resetResultEffect();
    setMessage(result === 'fail'
      ? `${serial} 已人工确认，当前任务已完成。`
      : `${serial} 已查看，当前任务已完成。`);
  }

  async function refreshCurrent(serial: string) {
    if (activeTaskCard?.display.requirementId) {
      const [response, pmiInspection] = await Promise.all([
        listFinalReleasePmiRequirementUnits(activeTaskCard.display.requirementId),
        resolvePmiInspectionSource(activeTaskCard.display),
      ]);
      const nextResult = buildPmiScanResultFromRequirementUnits({
        grade: activeTaskCard.display.grade,
        pmiInspection,
        requirementId: activeTaskCard.display.requirementId,
        taskNo: activeTaskCard.display.taskNo,
      }, response.data, serial);
      const nextCard = {
        ...activeTaskCard,
        display: {
          ...activeTaskCard.display,
          pmiInspection,
        },
        result: nextResult,
      };

      setScanResult(nextResult);
      setActiveTaskCard(nextCard);
      setTaskCards((cards) => cards.map((item) => (
        item.key === nextCard.key ? nextCard : item
      )));
      markWaitingFromNow();
      return nextResult;
    }

    const response = await scanPmiCode(serial);
    setScanResult(response.data);
    if (!cacheActiveTaskResult(response.data)) {
      setTaskCards((cards) => mergeTaskCards(cards, response.data));
    }
    markWaitingFromNow();
    return response.data;
  }

  async function handleEnterTaskCard(card: PmiTaskCard, options?: { selectFirstUnit?: boolean }) {
    if (card.result) {
      const pmiInspection = await resolvePmiInspectionSource(card.display);
      const resultWithRanges = applyPmiInspectionSource(card.result, pmiInspection);
      const firstSerial = options?.selectFirstUnit ? getPmiUnitSerial(resultWithRanges.units?.[0]) : '';
      const nextResult = firstSerial ? selectPmiScanResultUnit(resultWithRanges, firstSerial) || resultWithRanges : resultWithRanges;
      const nextCard = {
        ...card,
        display: {
          ...card.display,
          pmiInspection,
        },
        result: nextResult,
      };
      setScanResult(nextResult);
      setActiveTaskCard(nextCard);
      setTaskCards((cards) => cards.map((item) => (
        item.key === card.key ? nextCard : item
      )));
      markWaitingFromNow();
      setSpectrumWaitingActive(false);
      setShowingElementDetail(false);
      setElementFilter('all');
      setPostResultAction(null);
      resetResultEffect();
      setMessage('');
      return;
    }

    const requirementId = card.display.requirementId;
    if (!requirementId) {
      notifyError('缺少 PMI 需求ID');
      return;
    }

    setLoading(true);
    try {
      const [response, pmiInspection] = await Promise.all([
        listFinalReleasePmiRequirementUnits(requirementId),
        resolvePmiInspectionSource(card.display),
      ]);
      const resultFromUnits = buildPmiScanResultFromRequirementUnits({
        grade: card.display.grade,
        pmiInspection,
        requirementId,
        taskNo: card.display.taskNo,
      }, response.data);
      const firstSerial = options?.selectFirstUnit ? getPmiUnitSerial(resultFromUnits.units?.[0]) : '';
      const nextResult = firstSerial ? selectPmiScanResultUnit(resultFromUnits, firstSerial) || resultFromUnits : resultFromUnits;
      const nextCard = {
        ...card,
        display: {
          ...card.display,
          pmiInspection,
        },
        result: nextResult,
      };

      setScanResult(nextResult);
      setActiveTaskCard(nextCard);
      setTaskCards((cards) => cards.map((item) => (
        item.key === card.key ? nextCard : item
      )));
      markWaitingFromNow();
      setSpectrumWaitingActive(false);
      setShowingElementDetail(false);
      setElementFilter('all');
      setPostResultAction(null);
      resetResultEffect();
      setMessage('');
    } catch (error: any) {
      notifyError(error?.msg || 'PMI 件位加载失败');
      setMessage(error?.msg || 'PMI 件位加载失败，请重试。');
    } finally {
      setLoading(false);
    }
  }

  async function findRemoteTaskCardByCode(code: string) {
    if (!isPmiTaskScanCode(code)) {
      return null;
    }

    const response = await listFinalReleasePmiRequirements({
      keyword: code,
      page: 1,
      page_size: 20,
    });
    const cards = extractPmiRequirementList(response.data)
      .map(buildPmiRequirementPoolItem)
      .filter((item): item is PmiRequirementPoolItem => Boolean(item))
      .map<PmiTaskCard>((item) => ({
        display: item,
        key: item.key,
        scanCode: item.scanCode,
      }));

    if (cards.length) {
      setTaskCards((currentCards) => [
        ...cards,
        ...currentCards.filter((card) => !cards.some((item) => item.key === card.key)),
      ]);
    }

    return findTaskCardByCode(cards, code) || (cards.length === 1 ? cards[0] : null);
  }

  async function findRemoteTaskCardByScanResult(result: PmiScanResult, code: string) {
    const lookupValues = [
      getScanResultRequirementId(result),
      asText(result.task_no),
      asText(result.pmi_task_no),
      asText(result.task?.task_no),
      asText(result.task?.pmi_task_no),
      code,
    ].map((value) => value.trim()).filter(Boolean);
    const uniqueLookupValues = Array.from(new Set(lookupValues));

    for (const keyword of uniqueLookupValues) {
      const response = await listFinalReleasePmiRequirements({
        keyword,
        page: 1,
        page_size: 20,
      });
      const cards = extractPmiRequirementList(response.data)
        .map(buildPmiRequirementPoolItem)
        .filter((item): item is PmiRequirementPoolItem => Boolean(item))
        .map<PmiTaskCard>((item) => ({
          display: item,
          key: item.key,
          scanCode: item.scanCode,
        }));

      if (!cards.length) {
        continue;
      }

      setTaskCards((currentCards) => [
        ...cards,
        ...currentCards.filter((card) => !cards.some((item) => item.key === card.key)),
      ]);

      const matchedCard = findTaskCardByScanResult(cards, result, code);
      if (matchedCard || cards.length === 1) {
        return matchedCard || cards[0];
      }
    }

    return null;
  }

  async function createHydratedScannedTaskCard(result: PmiScanResult, code: string) {
    const matchedCard = findTaskCardByScanResult(taskCards, result, code)
      || await findRemoteTaskCardByScanResult(result, code);
    if (!matchedCard) {
      return null;
    }

    const pmiInspection = await resolvePmiInspectionSource(matchedCard.display);
    const resultWithMetadata = applyTaskCardMetadataToScanResult(result, matchedCard);
    const resultWithRanges = applyPmiInspectionSource(resultWithMetadata, pmiInspection);

    return {
      ...matchedCard,
      display: {
        ...matchedCard.display,
        pmiInspection,
      },
      result: resultWithRanges,
    };
  }

  async function handleTaskCodeScan(code: string) {
    const localCard = findTaskCardByCode(taskCards, code);
    if (localCard) {
      await handleEnterTaskCard(localCard, { selectFirstUnit: true });
      setScanValue('');
      return true;
    }

    const remoteCard = await findRemoteTaskCardByCode(code);
    if (!remoteCard) {
      return false;
    }

    await handleEnterTaskCard(remoteCard, { selectFirstUnit: true });
    setScanValue('');
    return true;
  }

  async function handleScanCode(rawCode: string) {
    const code = normalizeScannedCode(rawCode);
    if (!code) {
      notifyError('请先扫码');
      return;
    }

    if (
      postResultAction?.result === 'fail'
      && postResultAction.serial.trim().toUpperCase() !== code.trim().toUpperCase()
    ) {
      notifyError('请先人工确认不合格件');
      return;
    }

    if (selectCurrentTaskUnit(code, { forceAwaiting: true })) {
      return;
    }

    setLoading(true);
    try {
      if (await handleTaskCodeScan(code)) {
        setMessage('');
        return;
      }

      const response = await scanPmiCode(code);
      const initialResult = response.data;
      const hydratedTaskCard = await createHydratedScannedTaskCard(initialResult, code);
      const initialTaskCard = hydratedTaskCard || createScannedTaskCard(initialResult);
      const pmiInspection = initialTaskCard ? await resolvePmiInspectionSource(initialTaskCard.display) : '';
      const nextResult = hydratedTaskCard?.result || applyPmiInspectionSource(initialResult, pmiInspection);
      const scannedTaskCard = hydratedTaskCard || createScannedTaskCard(nextResult);
      setScanResult(nextResult);
      if (scannedTaskCard) {
        setActiveTaskCard(scannedTaskCard);
      }
      if (hydratedTaskCard) {
        setTaskCards((cards) => cards.map((card) => (
          card.key === hydratedTaskCard.key ? hydratedTaskCard : card
        )));
      }
      markWaitingFromNow();
      setSpectrumWaitingActive(false);
      setShowingElementDetail(false);
      setElementFilter('all');
      setPostResultAction(null);
      resetResultEffect();
      setMessage('');
      setScanValue('');
    } catch (error: any) {
      notifyError(error?.msg || 'PMI 扫码失败');
      setMessage(error?.msg || 'PMI 扫码失败，请重扫。');
    } finally {
      setLoading(false);
    }
  }

  function handleSelectDetailUnit(unit: PmiUnit) {
    const serial = getPmiUnitSerial(unit);
    if (!serial) {
      notifyError('该件缺少检测流水号');
      return;
    }
    if (
      postResultAction?.result === 'fail'
      && postResultAction.serial.trim().toUpperCase() !== serial.trim().toUpperCase()
    ) {
      notifyError('请先人工确认不合格件');
      return;
    }

    if (!selectCurrentTaskUnit(serial)) {
      notifyError('当前任务中未找到该流水号');
    }
  }

  function handleOpenUnitElementDetail(unit: PmiUnit) {
    const serial = getPmiUnitSerial(unit);
    if (!serial) {
      notifyError('该件缺少检测流水号');
      return;
    }

    const selectedResult = selectPmiScanResultUnit(scanResult, serial);
    if (!selectedResult) {
      notifyError('当前任务中未找到该流水号');
      return;
    }

    setScanResult(selectedResult);
    cacheActiveTaskResult(selectedResult);
    setSpectrumWaitingActive(false);
    setElementFilter('all');
    setShowingElementDetail(true);
  }

  async function bindSpectrum(raw: unknown) {
    const display = currentDisplayRef.current;
    if (!display?.serial || bindingRef.current || !spectrumWaitingRef.current) {
      return;
    }

    if (!isPmiSpectrumWsDataFresh(raw, waitingSinceRef.current)) {
      return;
    }

    const payload = buildPmiSpectrumPayloadFromWsData(raw, display.serial, display.grade);
    if (!payload) {
      setMessage('收到光谱数据，但没有可绑定的元素结果。');
      return;
    }

    const spectrumKey = buildPmiSpectrumPayloadKey(payload);
    if (hasRecentlyConsumedSpectrumKey(spectrumKey)) {
      setMessage('已忽略重复光谱数据，等待下一次测量。');
      return;
    }

    bindingRef.current = true;
    setBinding(true);
    try {
      const response = await bindPmiSpectrum(payload);
      markSpectrumKeyConsumed(spectrumKey);
      const boundData = response.data;
      let resultForDecision: PmiScanResult | null = null;
      if (isScanResult(boundData)) {
        resultForDecision = boundData;
      } else {
        const refreshedResult = await refreshCurrent(display.serial);
        if (refreshedResult) {
          resultForDecision = refreshedResult;
        }
      }
      const completedUnit = resultForDecision ? findPmiUnitBySerial(resultForDecision, payload.inspection_serial) : undefined;
      const completedResult = completedUnit ? getPmiUnitResult(completedUnit) : null;
      if (resultForDecision) {
        const resultKind = completedResult === 'fail' ? 'fail' : 'pass';
        syncCompletedResultForElementDetail(resultForDecision, payload.inspection_serial, resultKind);
        setMessage('');
      }
    } catch (error: any) {
      notifyError(error?.msg || '光谱绑定失败');
      setMessage(error?.msg || '光谱绑定失败，请重新检测。');
    } finally {
      setSpectrumWaitingActive(false);
      bindingRef.current = false;
      setBinding(false);
    }
  }

  bindSpectrumRef.current = bindSpectrum;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void handleScanCode(scanValue);
  }

  function handleRedo() {
    if (!currentDisplay?.serial) {
      notifyError('请先扫码');
      return;
    }
    setSpectrumWaitingActive(false);
    setPostResultAction(null);
    resetResultEffect();
    void handleScanCode(currentDisplay.serial);
  }

  function handleStartWaiting() {
    if (!currentDisplay?.serial) {
      notifyError('请先扫码');
      return;
    }
    if (postResultAction?.result === 'fail') {
      notifyError('请先人工确认不合格件');
      return;
    }
    if (!currentDisplay.waiting) {
      notifyError('当前件已完成，可重新检测后再等待。');
      return;
    }
    markWaitingFromNow();
    setSpectrumWaitingActive(true);
    setMessage('');
  }

  function handleReturnToPool() {
    setSpectrumWaitingActive(false);
    setShowingElementDetail(false);
    setElementFilter('all');
    setActiveTaskCard(null);
    setPostResultAction(null);
    resetResultEffect();
    setMessage('');
  }

  function handleDeviceChange(deviceId: string) {
    setSelectedDeviceId(deviceId);
    setDevicePickerOpen(false);
    setSpectrumWaitingActive(false);
    markWaitingFromNow();
    setPostResultAction(null);
    resetResultEffect();
    setMessage('');
  }

  function handlePageBack() {
    if (resultEffect) {
      resetResultEffect();
      setShowingElementDetail(true);
      return;
    }

    if (showingElementDetail) {
      setPostResultAction(null);
      setElementFilter('all');
      setShowingElementDetail(false);
      return;
    }

    if (showingDetail) {
      handleReturnToPool();
      return;
    }

    navigateTo('/home');
  }

  useEffect(() => {
    void loadTaskPool();
    void loadXrfDevices();
  }, []);

  useEffect(() => {
    if (!showingDetail || !currentDisplay?.serial) {
      return;
    }

    activeUnitRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [showingDetail, currentDisplay?.serial]);

  useEffect(() => {
    if (!selectedDeviceId) {
      setWsConnected(false);
      return undefined;
    }
    let active = true;
    let socket: WebSocket | null = null;
    markWaitingFromNow();
    setWsConnected(false);

    try {
      socket = new WebSocket(buildPmiSpectrumWsUrl({
        deviceId: selectedDeviceId,
        explicitUrl: PMI_SPECTRUM_WS_URL,
      }));
    } catch {
      setWsConnected(false);
      return undefined;
    }

    socket.onopen = () => {
      if (active) {
        setWsConnected(true);
      }
    };
    socket.onclose = () => {
      if (active) {
        setWsConnected(false);
      }
    };
    socket.onerror = () => {
      if (active) {
        setWsConnected(false);
      }
    };
    socket.onmessage = (event) => {
      if (active) {
        void bindSpectrumRef.current(event.data);
      }
    };

    return () => {
      active = false;
      if (socket) {
        socket.onopen = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.onmessage = null;
      }
      socket?.close();
    };
  }, [selectedDeviceId]);

  useEffect(() => () => {
    clearResultEffectTimer();
  }, []);

  const postResultButtonText = postResultAction?.result === 'fail'
    ? postResultAction.nextSerial ? '人工确认，下一件' : '人工确认完成'
    : postResultAction?.nextSerial ? '下一件' : '完成';
  const currentStatus = (() => {
    if (binding) {
      return { text: '绑定中', tone: 'orange' };
    }
    if (currentDisplay?.waiting) {
      return { text: spectrumWaiting ? '等待光谱' : '待开始', tone: 'orange' };
    }
    if (currentElementDetails.length) {
      if (elementResultState === 'fail') {
        return { text: '不合格', tone: 'red' };
      }
      if (elementResultState === 'pass') {
        return { text: '合格', tone: 'green' };
      }
      return { text: '未判定', tone: 'blue' };
    }
    return { text: currentDisplay?.stateText || '未开始', tone: 'blue' };
  })();
  const completionBanner = (() => {
    if (!currentTaskCompleted) {
      return null;
    }
    if (currentElementDetails.length) {
      return null;
    }
    return {
      className: 'pmi-waiting-panel pmi-waiting-panel--complete',
      text: '当前任务已完成',
    };
  })();

  return (
    <PdaPage
      actionLabel="刷新"
      onAction={() => {
        void loadXrfDevices();
        if ((showingDetail || showingElementDetail) && currentDisplay?.serial) {
          void refreshCurrent(currentDisplay.serial);
        } else {
          void loadTaskPool();
        }
      }}
      onBack={handlePageBack}
      pageClassName="pmi-scan-page"
      subtitle={`${user?.full_name || user?.username || '未命名员工'}${user?.emp_no ? ` · ${user.emp_no}` : ''}`}
      title={showingElementDetail ? '元素详情' : showingDetail ? 'PMI 任务详情' : 'PMI 扫码'}
    >
      <AnimatePresence>
        {resultEffect ? (
          <motion.div
            animate={{ opacity: 1 }}
            className={`pmi-result-effect pmi-result-effect--${resultEffect.result}`}
            exit={{ opacity: 0, scale: 1.02 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <motion.div
              animate={{
                scale: [0.96, 1.02, 1],
              }}
              className="pmi-result-effect__visual"
              initial={{ opacity: 0, scale: 0.84 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="pmi-result-effect__field pmi-result-effect__field--left" aria-hidden="true" />
              <span className="pmi-result-effect__field pmi-result-effect__field--right" aria-hidden="true" />
              <span className="pmi-result-effect__ring pmi-result-effect__ring--outer" aria-hidden="true" />
              <span className="pmi-result-effect__ring pmi-result-effect__ring--dash" aria-hidden="true" />
              <span className="pmi-result-effect__ring pmi-result-effect__ring--scan" aria-hidden="true" />
              <span className="pmi-result-effect__ring pmi-result-effect__ring--inner" aria-hidden="true" />
              <span className="pmi-result-effect__orbit-light pmi-result-effect__orbit-light--one" aria-hidden="true" />
              <span className="pmi-result-effect__orbit-light pmi-result-effect__orbit-light--two" aria-hidden="true" />
              <motion.img
                alt=""
                animate={{ filter: ['blur(2px)', 'blur(0px)'], opacity: [0, 1], y: [10, 0] }}
                className="pmi-result-effect__device"
                initial={{ filter: 'blur(3px)', opacity: 0, y: 16 }}
                src={`${process.env.PUBLIC_URL || ''}/assets/pmi-spectrometer.svg`}
                transition={{ delay: 0.12, duration: 0.5, ease: 'easeOut' }}
              />
              <motion.span
                animate={{ opacity: [0, 1], scale: [0.72, 1.08, 1] }}
                className={`pmi-result-effect__badge pmi-result-effect__badge--${resultEffect.result}`}
                initial={{ opacity: 0, scale: 0.72 }}
                transition={{ delay: 0.25, duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
              >
                <span aria-hidden="true" />
                <span aria-hidden="true" />
              </motion.span>
              {resultEffect.result === 'fail' ? (
                <motion.span
                  animate={{ opacity: [0, 1], rotate: [-8, 4, 0], scale: [0.72, 1.1, 1] }}
                  className="pmi-result-effect__warning"
                  initial={{ opacity: 0, rotate: -12, scale: 0.72 }}
                  transition={{ delay: 0.36, duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span aria-hidden="true">!</span>
                </motion.span>
              ) : null}
            </motion.div>
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="pmi-result-effect__copy"
              initial={{ opacity: 0, y: 12 }}
              transition={{ delay: 0.24, duration: 0.34 }}
            >
              <motion.div
                animate={{ opacity: 1, scale: [0.94, 1.04, 1] }}
                className="pmi-result-effect__title"
                initial={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: 0.32, duration: 0.36 }}
              >
                {resultEffect.result === 'fail' ? '不合格' : '合格'}
              </motion.div>
              <div className="pmi-result-effect__hint">
                {resultEffect.result === 'fail'
                  ? '检测未通过，正在进入元素详情'
                  : '检测通过，正在进入元素详情'}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {showingElementDetail ? (
        <PmiElementComparison
          filter={elementFilter}
          items={currentElementDetails}
          onFilterChange={setElementFilter}
          footer={(
            <button
              className="pmi-primary-btn"
              onClick={postResultAction ? handlePostResultContinue : () => setShowingElementDetail(false)}
              type="button"
            >
              {postResultAction ? postResultButtonText : '返回检测结果'}
            </button>
          )}
        />
      ) : (
        <>
          <section className="pmi-card pmi-scan-card">
            <div className="pmi-block-title">
              <h2>请扫二维码/条形码</h2>
              <div className="pmi-device-picker">
                <button
                  className={wsConnected ? 'pmi-device-pill' : 'pmi-device-pill pmi-device-pill--offline'}
                  disabled={deviceLoading || !xrfDevices.length}
                  onClick={() => setDevicePickerOpen((open) => !open)}
                  type="button"
                >
                  {selectedDeviceText} · {wsConnected ? '已连接' : '未连接'}
                </button>
                {devicePickerOpen ? (
                  <div className="pmi-device-sheet">
                    <div className="pmi-device-sheet-head">
                      <strong>选择光谱仪</strong>
                      <span>当前 {selectedDeviceText}</span>
                    </div>
                    {xrfDevices.map((device) => {
                      const active = selectedDeviceId === device.deviceId;
                      return (
                        <button
                          className={active ? 'pmi-device-option pmi-device-option--active' : 'pmi-device-option'}
                          key={device.deviceId}
                          onClick={() => handleDeviceChange(device.deviceId)}
                          type="button"
                        >
                          <span className="pmi-device-option-copy">
                            <span className="pmi-device-option-name">{device.deviceId}</span>
                          </span>
                          <span className={active ? 'pmi-device-option-state pmi-device-option-state--active' : 'pmi-device-option-state pmi-device-option-state--idle'}>
                            {active ? '已选' : '切换'}
                          </span>
                        </button>
                      );
                    })}
                    {!xrfDevices.length ? (
                      <div className="pmi-device-empty">暂无可用光谱仪。</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <form className="pmi-scan-form" onSubmit={handleSubmit}>
              <input
                autoFocus
                className="pmi-scan-input"
                disabled={loading}
                onChange={(event) => setScanValue(event.target.value)}
                placeholder="任务码或单件 PMI 流水号"
                value={scanValue}
              />
              <button className="pmi-scan-submit" disabled={loading} type="submit">
                {loading ? '定位' : '扫码'}
              </button>
            </form>

            {message ? <div className="pmi-message">{message}</div> : null}
          </section>

          {showingDetail ? (
            <>
              <section className="pmi-card pmi-current-card">
                <div className="pmi-block-title">
                  <h2>当前检测</h2>
                  <span className={`pmi-status pmi-status--${currentStatus.tone}`}>
                    {currentStatus.text}
                  </span>
                </div>

            {currentDisplay ? (
              <>
                <div className="pmi-current-serial">{currentDisplay.serial}</div>
                <div className="pmi-current-summary">
                  第 {currentDisplay.currentIndex} 件，共 {currentDisplay.total} 件。
                  已完成 {currentDisplay.completed} 件，还剩 {currentDisplay.remaining} 件。
                </div>

                <div className="pmi-progress-line">
                  <div className="pmi-progress-copy">
                    <span>任务进度</span>
                    <span>{currentDisplay.completed} / {currentDisplay.total}</span>
                  </div>
                  <div className="pmi-progress-track">
                    <div
                      className="pmi-progress-fill"
                      style={{ width: `${Math.round((currentDisplay.completed / currentDisplay.total) * 100)}%` }}
                    />
                  </div>
                </div>

                {binding || (currentDisplay.waiting && spectrumWaiting) ? (
                  <div className={binding ? 'pmi-waiting-panel pmi-waiting-panel--binding' : 'pmi-waiting-panel'}>
                    <span aria-hidden="true" className="pmi-waiting-dots">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span>{binding ? '正在绑定光谱数据' : '等待光谱仪数据'}</span>
                  </div>
                ) : null}

                {completionBanner ? (
                  <div className={completionBanner.className}>
                    {completionBanner.text}
                  </div>
                ) : null}

              </>
            ) : (
              <div className="pmi-empty">请先扫码或从任务池进入当前检测件。</div>
            )}

            <div className={currentDisplay?.waiting && !currentTaskCompleted ? 'pmi-big-actions' : 'pmi-big-actions pmi-big-actions--single'}>
              {currentDisplay?.waiting && !currentTaskCompleted ? (
                <button
                  className="pmi-primary-btn"
                  disabled={!currentDisplay || loading || binding || spectrumWaiting}
                  onClick={handleStartWaiting}
                  type="button"
                >
                  {spectrumWaiting ? '等待中' : '开始等待'}
                </button>
              ) : null}
              <button className="pmi-secondary-btn" disabled={!currentDisplay || loading || binding} onClick={handleRedo} type="button">
                重新检测
              </button>
            </div>
              </section>

              <section className="pmi-card pmi-detail-card">
            <div className="pmi-detail-head">
              <div className="pmi-detail-title">
                <h2>任务详情</h2>
                <p>
                  {activeTaskCard?.display.taskNo || '--'} · {detailUnits.length || activeTaskCard?.display.total || 0} 个检测流水号
                </p>
              </div>
              <button
                className="pmi-detail-back"
                onClick={handleReturnToPool}
                type="button"
              >
                任务池
              </button>
            </div>

            <div className="pmi-detail-meta">
              <div>订单 {activeTaskCard?.display.orderNo || '--'} / {activeTaskCard?.display.itemNo || '--'}</div>
              <div>物料 {activeTaskCard?.display.materialNo || '--'} · 材质 {activeTaskCard?.display.grade || '--'}</div>
            </div>

            <div className="pmi-unit-list">
              {detailUnits.length ? detailUnits.map((unit, index) => {
                const serial = getPmiUnitSerial(unit) || `第 ${index + 1} 件`;
                const active = currentDisplay?.serial === serial;
                const bound = isDisplayUnitBound(unit);
                const unitDisplay = getPmiUnitDisplayState(unit, active, spectrumWaiting);
                const unitItemClassName = [
                  'pmi-unit-item',
                  `pmi-unit-item--${unitDisplay.tone}`,
                  active ? 'pmi-unit-item--active' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div
                    className={unitItemClassName}
                    key={`${serial}-${index}`}
                    ref={active ? activeUnitRef : undefined}
                  >
                    <button
                      className="pmi-unit-select"
                      disabled={Boolean(
                        postResultAction?.result === 'fail'
                        && postResultAction.serial.trim().toUpperCase() !== serial.trim().toUpperCase(),
                      )}
                      onClick={() => handleSelectDetailUnit(unit)}
                      type="button"
                    >
                      <span className="pmi-unit-copy">
                        <span className="pmi-unit-label">{getDetailUnitLabel(unit, index)}</span>
                        <span className="pmi-unit-serial">{serial}</span>
                      </span>
                      <span className={`pmi-unit-state pmi-unit-state--${unitDisplay.tone}`}>
                        {unitDisplay.text}
                      </span>
                    </button>
                    {bound ? (
                      <button
                        className="pmi-unit-detail-btn"
                        onClick={() => handleOpenUnitElementDetail(unit)}
                        type="button"
                      >
                        详情
                      </button>
                    ) : null}
                  </div>
                );
              }) : (
                <div className="pmi-empty">这个任务还没有检测流水号。</div>
              )}
            </div>
              </section>
            </>
          ) : (
            <section className="pmi-card pmi-pool-card">
              <div className="pmi-pool-head">
                <h2>任务池</h2>
                <span>{poolLoading ? '加载中' : `${taskCards.length} 个任务`}</span>
              </div>
              <div className="pmi-task-list">
                {taskCards.length ? taskCards.map((card) => (
                  <article className={`pmi-task-item pmi-task-item--${card.display.statusTone}`} key={card.key}>
                    <div className="pmi-task-main">
                      <div className="pmi-task-top">
                        <div className="pmi-task-no">{card.display.taskNo}</div>
                        <span className={`pmi-task-status pmi-task-status--${card.display.statusTone}`}>
                          {card.display.statusText}
                        </span>
                      </div>
                      <div className="pmi-task-line">
                        {card.display.customerCode || '--'} · 订单 {card.display.orderNo || '--'}
                        {card.display.itemNo ? ` / ${card.display.itemNo}` : ''}
                      </div>
                      <div className="pmi-task-line pmi-task-line--muted">
                        物料 {card.display.materialNo || '--'} · {card.display.grade || '--'} · 当前{' '}
                        {card.display.currentQuantity || card.display.total}
                      </div>
                      <div className="pmi-task-counts">
                        合格 {card.display.passedCount} · 不合格 {card.display.failedCount}
                      </div>
                    </div>
                    <button
                      className="pmi-task-enter"
                      onClick={() => {
                        void handleEnterTaskCard(card);
                      }}
                      type="button"
                    >
                      进入
                    </button>
                  </article>
                )) : (
                  <div className="pmi-empty">扫码后显示任务。</div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </PdaPage>
  );
}

export default PmiScan;
